import {
	BarChart3,
	BookOpenText,
	ClipboardList,
	FileText,
	Globe,
	GraduationCap,
	HelpCircle,
	LayoutGrid,
	MapIcon,
	Repeat,
	Settings,
	Tags,
	UsersRound,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import logo from '@/assets/logo.svg';
import { showErrorToast } from '@/components/ToastNotification';
import { useAuth } from '@/hooks/useAuth';
import useOutsideClick from '@/hooks/useOutsideClick';
import { usePermissions } from '@/hooks/usePermissions';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';

declare const __APP_VERSION__: string;

interface NavItemDef {
	to: string;
	label: string;
	icon: React.ElementType;
	external?: boolean;
}

interface NavGroup {
	label: string;
	items: NavItemDef[];
}

// ── Hover pill that appears in icon-rail mode (< 1200px) ────────────────────
const HoverPill = ({ label }: { label: string }) => (
	<span
		aria-hidden
		className={cn(
			'hidden',
			'absolute left-full top-1/2 -translate-y-1/2 ml-2',
			'px-2.5 py-1 rounded-md bg-gray-900 text-white',
			'text-xs font-medium whitespace-nowrap',
			'opacity-0 group-hover:opacity-100 transition-opacity duration-100',
			'z-50 pointer-events-none select-none'
		)}
	>
		{label}
	</span>
);

// ── Single nav item ──────────────────────────────────────────────────────────
const SidebarItem = ({ to, label, icon: Icon, external }: NavItemDef) => {
	const linkBase = cn(
		'flex items-center justify-center condensed:justify-start gap-2.5',
		'rounded-md px-2.5 py-1.5 transition-colors duration-150 no-underline w-full'
	);

	if (external) {
		return (
			<div className="relative group">
				<a
					href={to}
					target="_blank"
					rel="noreferrer"
					aria-label={label}
					className={cn(
						linkBase,
						'text-muted-foreground hover:text-(--icon-hover) hover:bg-orange-50'
					)}
				>
					<Icon size={20} strokeWidth={1.4} className="shrink-0" />
					<span className="hidden condensed:inline text-[12.5px] font-medium leading-none text-muted-foreground">
						{label}
					</span>
				</a>
				<HoverPill label={label} />
			</div>
		);
	}

	return (
		<div className="relative group">
			<NavLink
				to={to}
				aria-label={label}
				className={({ isActive }) =>
					cn(
						linkBase,
						isActive
							? 'text-(--icon-hover) bg-orange-50'
							: 'text-muted-foreground hover:text-(--icon-hover) hover:bg-orange-50'
					)
				}
			>
				{({ isActive }) => (
					<>
						<Icon size={20} strokeWidth={1.4} className="shrink-0" />
						<span
							className={cn(
								'hidden condensed:inline text-[12.5px] font-medium leading-none',
								!isActive && 'text-muted-foreground'
							)}
						>
							{label}
						</span>
					</>
				)}
			</NavLink>
			<HoverPill label={label} />
		</div>
	);
};

// ── Section label ────────────────────────────────────────────────────────────
const GroupLabel = ({ label }: { label: string }) => (
	<span className="hidden condensed:block px-2.5 pt-1 pb-0.5 text-xxs font-semibold tracking-wider uppercase text-muted-foreground/60 select-none">
		{label}
	</span>
);

// ── Avatar + sign-out menu, pinned above the footer ──────────────────────────
const SidebarUser = () => {
	const { user } = useAuth();
	const [isOpen, setIsOpen] = useState(false);
	const menuRef = useRef<HTMLDivElement>(document.createElement('div'));
	const navigate = useNavigate();

	useOutsideClick(menuRef, () => {
		if (isOpen) setIsOpen(false);
	});

	if (!user) return null;

	const signOut = async () => {
		setIsOpen(false);
		const { error } = await supabase.auth.signOut();
		if (error) {
			showErrorToast('Unable to sign out...please try again.');
			return;
		}
		navigate('/');
	};

	return (
		<div className="relative px-2 pb-2" ref={menuRef}>
			<button
				type="button"
				data-testid="avatar"
				onClick={() => setIsOpen(!isOpen)}
				className="flex items-center justify-center condensed:justify-start gap-2.5 w-full rounded-md px-2.5 py-1.5 bg-transparent border-0 cursor-pointer transition-colors duration-150 hover:bg-orange-50 focus:outline-none"
			>
				<div
					className="flex items-center justify-center shrink-0 h-7 w-7 rounded-full bg-[#737295] text-white text-xs leading-none select-none"
					style={{ fontFamily: 'var(--font-semibold)' }}
				>
					{user.email?.split('')[0].toUpperCase()}
				</div>
				<span className="hidden condensed:inline max-w-28 truncate text-[12.5px] font-medium leading-none text-muted-foreground">
					{user.email}
				</span>
			</button>
			{isOpen && (
				<div className="absolute bottom-full left-2 mb-1 min-w-30 overflow-hidden rounded-lg border border-(--input-border) bg-white shadow-[0_4px_16px_rgba(0,0,0,0.1)] z-50">
					<button
						type="button"
						onClick={signOut}
						className="w-full px-4 py-2 text-left text-sm bg-transparent border-0 cursor-pointer text-(--label-text) transition-colors hover:bg-(--elevate-primary) hover:text-white focus:bg-(--elevate-primary) focus:text-white focus:outline-none"
						style={{ fontFamily: 'var(--font-semibold)' }}
					>
						Sign Out
					</button>
				</div>
			)}
		</div>
	);
};

// ── Main component ───────────────────────────────────────────────────────────
const SidebarNav = () => {
	const {
		isLoading,
		canManageUsers,
		canManageTags,
		canManageDisciplines,
		canManageDivisions,
		canViewStats,
		canViewCourseMap,
		canManageSettings,
	} = usePermissions();

	if (isLoading) return null;

	// Build groups, omitting empty ones
	const mainGroups: NavGroup[] = [
		{
			label: 'Curriculum',
			items: [
				{ to: '/dashboard/courses', label: 'Courses', icon: GraduationCap },
				{ to: '/dashboard/submissions', label: 'Submissions', icon: ClipboardList },
				{ to: '/dashboard/course-alignment', label: 'Alignment', icon: Repeat },
				...(canViewCourseMap
					? [{ to: '/dashboard/course-map', label: 'Course Map', icon: MapIcon }]
					: []),
			],
		},
		...(canManageUsers || canManageTags || canManageDisciplines || canManageDivisions
			? [
					{
						label: 'Admin',
						items: [
							...(canManageUsers
								? [{ to: '/dashboard/users', label: 'Users', icon: UsersRound }]
								: []),
							...(canManageTags ? [{ to: '/dashboard/tags', label: 'Tags', icon: Tags }] : []),
							...(canManageDisciplines
								? [{ to: '/dashboard/disciplines', label: 'Disciplines', icon: BookOpenText }]
								: []),
							...(canManageDivisions
								? [{ to: '/dashboard/divisions', label: 'Divisions', icon: LayoutGrid }]
								: []),
						],
					},
				]
			: []),
		...(canViewStats
			? [
					{
						label: 'Analytics',
						items: [
							{ to: '/dashboard/stats', label: 'Stats', icon: BarChart3 },
							{ to: '/dashboard/reports', label: 'Reports', icon: FileText },
						],
					},
				]
			: []),
	];

	const systemItems: NavItemDef[] = [
		...(canManageSettings
			? [{ to: '/dashboard/settings', label: 'Settings', icon: Settings }]
			: []),
		{
			to: 'https://support.elevatemap.app/welcome',
			label: 'Knowledge Base',
			icon: HelpCircle,
			external: true,
		},
		{ to: '/', label: 'Public View', icon: Globe },
	];

	return (
		<nav
			style={{ gridArea: 'sidebar' }}
			className="h-full bg-white border-r border-grey-100 flex flex-col"
		>
			{/* ── Logo ── */}
			<div className="flex items-center justify-center condensed:justify-start px-3 condensed:px-4 pt-5 pb-4">
				{/* Collapsed: feather mark */}
				<img src="/feather.svg" alt="" aria-hidden className="block condensed:hidden h-8 w-auto" />
				{/* Expanded: wordmark */}
				<img src={logo} alt="Elevate" className="hidden condensed:block w-[110px] h-auto" />
			</div>

			{/* ── Main groups (scrollable) ── */}
			<div className="flex-1 flex flex-col gap-4 overflow-y-auto px-2 pb-2 min-h-0">
				{mainGroups.map((group) => (
					<div key={group.label} className="flex flex-col gap-0.5">
						<GroupLabel label={group.label} />
						{group.items.map((item) => (
							<SidebarItem key={item.to} {...item} />
						))}
					</div>
				))}
				{/* ── System group (pinned to bottom) ── */}
			<div className="flex flex-col gap-0.5">
				<GroupLabel label="System" />
				{systemItems.map((item) => (
					<SidebarItem key={item.to} {...item} />
				))}
			</div>
			</div>

			

			{/* ── Signed-in user ── */}
			<div className="border-t border-grey-100 pt-2">
				<SidebarUser />
			</div>

			{/* ── Footer (expanded only) ── */}
			<div className="hidden condensed:block px-4 pb-3 text-xxs text-muted-foreground/50 select-none">
				© {new Date().getFullYear()} Stakk IO v{__APP_VERSION__}
			</div>
		</nav>
	);
};

export default SidebarNav;
